import { createContext, useCallback, useMemo, useState } from "react";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnAdmin, isMemberAPracticeManager, isSystemAccount } from "@/core/auth";
import { isCancellationChargeable } from "@/utils/cancellationCharge";
import { toLocalIsoDate } from "@/utils/toLocalIsoDate";
import { useCancelBooking } from "@/hooks/useCancelBooking";
import { useCancelBookingFromDate } from "@/hooks/useCancelBookingFromDate";
import type { Booking } from "@/types/Booking";
import type { CancelBookingDialogState, CancelResult, CancelScreenId } from "./types";

export const CancelBookingDialogContext = createContext<CancelBookingDialogState | null>(null);

const WEEKDAY_INDEX: Record<string, number> = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

function recurrenceWeekdays(booking: Booking): number[] {
  const rec = booking.recurrence ?? booking.recurrencePattern;
  const byWeekday = rec?.byWeekday;
  if (byWeekday && byWeekday.length) return byWeekday.map((w) => WEEKDAY_INDEX[w]).filter((n) => n !== undefined);
  // `date` is stored as UTC-midnight of the centre's day — getUTCDay so a viewer west of UTC
  // doesn't read the previous day's weekday (see recurrenceSummary.ts's identical fallback).
  return [new Date(booking.date).getUTCDay()];
}

export const CancelBookingDialogProvider = ({
  booking,
  onClose,
  children,
}: {
  booking: Booking;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const { member } = useMember();
  const isElevatedUser = useMemo(
    () => isMemberAnAdmin(member) || isMemberAPracticeManager(member) || isSystemAccount(member),
    [member],
  );
  // C6 admin exemption — admins (and system/superadmin) may cancel a slot immediately (from today);
  // Practice Managers remain bound by the calendar-month picker. See docs/CANCELLATION_RULES.md (C6).
  const isAdmin = useMemo(() => isMemberAnAdmin(member) || isSystemAccount(member), [member]);
  const isSlot = booking.type === "slot";
  // Slots are inherently weekly even when their `recurrence` document is missing (legacy/imported),
  // so treat them as recurring — otherwise the dialog skips the from-date screen and an elevated user
  // can't pick a date/month to cancel from. The backend cancel-from synthesises the recurrence to match.
  const isRecurring =
    Boolean(booking.recurrence?.frequency || booking.recurrencePattern?.frequency) || isSlot;

  const initialScreen: CancelScreenId = isSlot && !isElevatedUser ? "slot-restricted" : "warn";
  const [screenHistory, setScreenHistory] = useState<CancelScreenId[]>([initialScreen]);
  const currentScreen = screenHistory[screenHistory.length - 1];

  const [fromDate, setFromDateState] = useState<Date | null>(null);

  // Advisory charge preview, mirroring the backend cancel-charge rule (C1/C6): the charge keys off
  // the earliest CANCELLED occurrence (>= the chosen from-date), NOT the booking generally.
  //
  // Bug 2a: for a RECURRING cancel the charge is unknowable until the member picks a from-date (the
  // WarnScreen is shown *before* the FromDateScreen). We must therefore NOT assume `fromDate ?? today`
  // here — doing so warned "you'll be charged" on the WarnScreen whenever *today* happened to be
  // within 24h of an occurrence, even when the member then chose a far-future date. So: until a
  // from-date is chosen we report not-chargeable; once chosen, we key off that date. Single bookings
  // have no from-date and key off their own occurrence.
  // We do NOT exempt elevated users here, so they still see the notice + waive when genuinely within 24h.
  // The ResultScreen uses the backend-authoritative wasChargedOnCancel for the final message.
  // Note: isCancellationChargeable reads booking.recurrence only — a recurrencePattern-only
  // booking (expanded occurrence) returns not-chargeable here. Safe: the dialog opens against the
  // root recurring booking; ResultScreen shows the backend-authoritative wasChargedOnCancel.
  const willBeCharged = useMemo(() => {
    if (isRecurring && !fromDate) return false;
    const from = isRecurring ? fromDate : null;
    return isCancellationChargeable(booking, { fromDate: from }).chargeable;
  }, [booking, isRecurring, fromDate]);
  const allowedWeekdays = useMemo(() => recurrenceWeekdays(booking), [booking]);
  // A waive only makes sense for an elevated user cancelling a genuinely chargeable occurrence.
  // This is the single source of truth the ConfirmScreen and submit() both key off, so a `waive`
  // left over from a previously-chargeable from-date is inert once the charge is gone.
  const canWaive = isElevatedUser && willBeCharged;
  const [waive, setWaive] = useState(false);
  const [waiveNote, setWaiveNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CancelResult | null>(null);

  const cancelMutation = useCancelBooking();
  const cancelFromDateMutation = useCancelBookingFromDate(booking.id);

  const navigateTo = useCallback((screen: CancelScreenId) => setScreenHistory((prev) => [...prev, screen]), []);
  const goBack = useCallback(() => setScreenHistory((prev) => (prev.length <= 1 ? prev : prev.slice(0, -1))), []);
  const setFromDate = useCallback((d: Date) => setFromDateState(d), []);

  const submit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Only waive when it is actually applicable (elevated + chargeable). A `waive` left true from
      // an earlier chargeable from-date must not reach the server once the charge is gone — that
      // would ship waive:true with an empty note and be rejected 400, with no note field on screen.
      const effectiveWaive = canWaive && waive;
      // Only send a note when actually waiving; trim so whitespace-only can't slip past the server.
      const noteToSend = effectiveWaive ? waiveNote.trim() : undefined;
      if (isRecurring) {
        const effectiveFrom = fromDate ?? new Date();
        const res = await cancelFromDateMutation.mutateAsync({ fromDate: toLocalIsoDate(effectiveFrom), waive: effectiveWaive, waiveNote: noteToSend });
        setResult({ cancelledCount: res.cancelledCount, wasCharged: res.wasChargedOnCancel, message: res.message });
      } else {
        const cancelled = await cancelMutation.mutateAsync({ bookingId: booking.id, waive: effectiveWaive, waiveNote: noteToSend });
        setResult({ cancelledCount: 1, wasCharged: Boolean(cancelled?.wasChargedOnCancel), message: "Booking cancelled." });
      }
      navigateTo("result");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cancellation failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [isRecurring, fromDate, canWaive, waive, waiveNote, cancelFromDateMutation, cancelMutation, booking.id, navigateTo]);

  const value: CancelBookingDialogState = useMemo(
    () => ({
      booking, isElevatedUser, isAdmin, isSlot, isRecurring, willBeCharged,
      currentScreen, navigateTo, goBack,
      fromDate, setFromDate, allowedWeekdays,
      canWaive, waive, setWaive, waiveNote, setWaiveNote,
      isSubmitting, error, result, submit, onClose,
    }),
    [booking, isElevatedUser, isAdmin, isSlot, isRecurring, willBeCharged, currentScreen, navigateTo, goBack, fromDate, setFromDate, allowedWeekdays, canWaive, waive, setWaive, waiveNote, setWaiveNote, isSubmitting, error, result, submit, onClose],
  );

  return <CancelBookingDialogContext.Provider value={value}>{children}</CancelBookingDialogContext.Provider>;
};
